import {
  DailyPnlItem,
  IdeaHistoryDef,
} from '../../../../../apis/idea/idea-api.def.ts'
import { IdeyaAgGridTable } from '../../../../../common/ag-grid/ideya-ag-grid-table.tsx'
import { useTranslation } from 'react-i18next'
import { AutoSizeStrategy, ColDef, RowStyle } from 'ag-grid-community'
import { useMemo } from 'react'
import {
  currencyValueFormatter,
  marketDateTimezoneFormatter,
} from '../../../../../utils/formatters.ts'
import { CustomCellRendererProps } from 'ag-grid-react'
import { IdeaDirectionTag } from '../../idea-list-table/idea-direction-tag.tsx'
import dayjs from 'dayjs'
import { IdeaActionLogItem } from './idea-action-log-table.def.ts'
import { GridReadyEvent, RowClassParams } from 'ag-grid-enterprise'
import { useAppSelector } from '../../../../../redux/hooks.ts'
import { selectThemeStyle } from '../../../../../features/setting/setting.slice.ts'
import { IdeaAlertType } from '../../../../../enums/idea-list.enum.ts'
import { StatusCellRenderer } from '../../common/order-status-cell-renderer.tsx'

interface IdeaActionLogTableProps {
  actionLog: IdeaHistoryDef
  dailyPnlData: DailyPnlItem[]
}

export const IdeaActionLogTable: React.FC<IdeaActionLogTableProps> = ({
  actionLog,
  dailyPnlData,
}) => {
  const { t } = useTranslation()
  const themeStyle = useAppSelector(selectThemeStyle)

  const getLatestAlert = (
    actionLog: IdeaHistoryDef,
    type: IdeaAlertType
  ): IdeaHistoryDef['actions'][number] | null => {
    const filtered = actionLog.actions
      .filter((a) => a.alertType === type && a.showAlert)
      .sort((a, b) => (a.sendingTime > b.sendingTime ? 1 : -1))

    return filtered[filtered.length - 1] ?? null
  }

  const latestAlert = useMemo(() => {
    const alerts = actionLog.actions
      .filter((a) => a.showAlert)
      .sort((a, b) => (a.sendingTime > b.sendingTime ? 1 : -1))

    return alerts[alerts.length - 1] ?? null
  }, [actionLog.actions])

  // precompute whether warnings should be hidden
  const hideWarnings = useMemo(() => {
    const infoAlert = getLatestAlert(actionLog, IdeaAlertType.Info)
    const warningAlert = getLatestAlert(actionLog, IdeaAlertType.Warning)

    return (
      infoAlert && warningAlert && infoAlert.filledResult === 'Fully Filled'
    )
  }, [actionLog])

  // Calculating row data
  let rowData: IdeaActionLogItem[] = useMemo(() => {
    return actionLog.actions.map((item) => {
      // Find matching daily PnL data for this date
      const matchingPnl = dailyPnlData.find((pnl) =>
        dayjs(pnl.positionDate).isSame(dayjs(item.sendingTime), 'day')
      )

      const isLatestAlert =
        !!latestAlert && latestAlert.sendingTime === item.sendingTime

      return {
        date: item.sendingTime,
        action: item.actionType,
        direction: actionLog.idea.direction,
        targetMarketValue: item.targetMarketValue,
        size: null,
        qty: null,
        price: null,
        target: item.target,
        stop: item.stop,
        fxRate: null,
        currency: actionLog.idea.tickerCurrency,
        state: item.state,
        conviction: item.conviction,
        horizon: item.horizon,
        tradeReason: item.tradeReason,
        fillStrategy: item.fillStrategy,
        transactionCostLocal: null,
        transactionCostUsd: null,
        liquidateReason: actionLog.idea.liquidateReason,
        borrowFee: null,
        marketTimeZone: actionLog.idea.marketTimeZone,
        overallCost: null,
        pnl: matchingPnl?.pnl || null,
        realizedPnl: matchingPnl?.realizedPnl || null,
        ideaExecutionId: null,
        showAlert: item.showAlert,
        alertMessage: item.alertMessage,
        alertType: item.alertType,
        filledResult: item.filledResult,
        hideWarnings,
        isLatestAlert,
      }
    })
  }, [
    dailyPnlData,
    actionLog.actions,
    actionLog.idea.direction,
    actionLog.idea.liquidateReason,
    actionLog.idea.marketTimeZone,
    actionLog.idea.tickerCurrency,
  ])

  for (const execution of actionLog.executions) {
    const tmpItem: IdeaActionLogItem = {
      date: execution.fillTime,
      action: execution.action,
      direction: actionLog.idea.direction,
      qty: execution.qty,
      price: execution.price,
      target: null,
      targetMarketValue: null,
      marketTimeZone: actionLog.idea.marketTimeZone,
      size: execution.size,
      stop: null,
      state: 'Filled',
      conviction: null,
      horizon: null,
      tradeReason: null,
      fxRate: execution.fxRate,
      currency: execution.currency,
      fillStrategy: execution.fillStrategy,
      borrowFee: execution.borrowFee,
      liquidateReason: actionLog.idea.liquidateReason,
      transactionCostLocal: execution.transactionCostLocal,
      transactionCostUsd: execution.transactionCostUsd,
      overallCost: execution.overallCost,
      ideaExecutionId: execution.ideaExecutionId,
    }

    // Prevent duplicate by checking for existing item
    const exists = rowData.some(
      (item) => item.ideaExecutionId === execution.ideaExecutionId
    )
    if (!exists) {
      const index =
        rowData.findIndex((item) => item.date <= execution.fillTime) + 1
      if (index === rowData.length) {
        rowData.push(tmpItem)
      } else {
        rowData = [...rowData.slice(0, index), tmpItem, ...rowData.slice(index)]
      }
    }
  }
  rowData = rowData.sort((a, b) => {
    return a.date > b.date ? 1 : -1
  })

  const columns: ColDef[] = useMemo(
    () => [
      {
        headerName: t(
          'idea_list.idea_modal.idea_action_log.table.columns.date'
        ),
        field: 'date',
        valueFormatter: (params) =>
          marketDateTimezoneFormatter(params, { showTimezone: true }),
      },
      {
        headerName: t(
          'idea_list.idea_modal.idea_action_log.table.columns.action'
        ),
        field: 'action',
      },
      {
        headerName: t(
          'idea_list.idea_modal.idea_action_log.table.columns.direction'
        ),
        field: 'direction',
        cellRenderer: (props: CustomCellRendererProps) => {
          if (props.value !== undefined) {
            return <IdeaDirectionTag direction={props.data.direction} />
          }
          return <></>
        },
      },
      {
        headerName: t(
          'idea_list.idea_modal.idea_action_log.table.columns.target_market_value'
        ),
        field: 'targetMarketValue',
        type: 'numberColumn',
        valueFormatter: (params) =>
          currencyValueFormatter(params, {
            decimal: 0,
            currency: 'USD',
          }),
      },
      {
        headerName: t(
          'idea_list.idea_modal.idea_action_log.table.columns.size'
        ),
        field: 'size',
        type: 'numberColumn',
        valueFormatter: (params) =>
          currencyValueFormatter(params, {
            decimal: 0,
            currency: 'USD',
          }),
      },
      {
        headerName: t(
          'idea_list.idea_modal.idea_action_log.table.columns.quantity'
        ),
        field: 'qty',
        type: 'numberColumn',
      },
      {
        headerName: t(
          'idea_list.idea_modal.idea_action_log.table.columns.price'
        ),
        field: 'price',
        type: 'numberColumn',
        valueFormatter: (params) =>
          currencyValueFormatter(params, {
            decimal: 2,
            currency: params.data.currency,
          }),
      },
      {
        headerName: t(
          'idea_list.idea_modal.idea_action_log.table.columns.target'
        ),
        field: 'target',
        type: 'numberColumn',
        valueFormatter: (params) =>
          currencyValueFormatter(params, {
            decimal: 2,
            currency: params.data.currency,
          }),
      },
      {
        headerName: t(
          'idea_list.idea_modal.idea_action_log.table.columns.stop'
        ),
        field: 'stop',
        type: 'numberColumn',
        valueFormatter: (params) =>
          currencyValueFormatter(params, {
            decimal: 2,
            currency: params.data.currency,
          }),
      },
      {
        headerName: t(
          'idea_list.idea_modal.idea_action_log.table.columns.fx_rate'
        ),
        field: 'fxRate',
        type: 'numberColumn',
        valueFormatter: (params) =>
          currencyValueFormatter(params, {
            decimal: 2,
            currency: '',
          }),
      },
      {
        headerName: t(
          'idea_list.idea_modal.idea_action_log.table.columns.currency'
        ),
        field: 'currency',
      },
      {
        headerName: t(
          'idea_list.idea_modal.idea_action_log.table.columns.order_status'
        ),
        field: 'state',
        cellRenderer: StatusCellRenderer,
      },
      {
        headerName: t(
          'idea_list.idea_modal.idea_action_log.table.columns.conviction'
        ),
        field: 'conviction',
      },
      {
        headerName: t(
          'idea_list.idea_modal.idea_action_log.table.columns.horizon'
        ),
        field: 'horizon',
      },
      {
        headerName: t(
          'idea_list.idea_modal.idea_action_log.table.columns.trade_reason'
        ),
        field: 'tradeReason',
      },
      {
        headerName: t(
          'idea_list.idea_modal.idea_action_log.table.columns.fill_strategy'
        ),
        field: 'fillStrategy',
      },
      {
        headerName: t(
          'idea_list.idea_modal.idea_action_log.table.columns.fill_status'
        ),
        field: 'filledResult',
      },
      {
        headerName: t(
          'idea_list.idea_modal.idea_action_log.table.columns.overall_cost'
        ),
        field: 'overallCost',
        type: 'numberColumn',
        valueFormatter: (params) =>
          currencyValueFormatter(params, {
            decimal: 0,
            currency: 'USD',
          }),
      },
      {
        headerName: t(
          'idea_list.idea_modal.idea_action_log.table.columns.liquidate_reason'
        ),
        field: 'liquidateReason',
      },
    ],
    [t]
  )

  const defaultColDef: ColDef = {
    sortable: false,
    filter: false,
    suppressHeaderMenuButton: true,
  }

  const autoSizeStrategy = {
    type: 'fitCellContents',
  } as AutoSizeStrategy

  const handleGridReady = (params: GridReadyEvent) => {
    params.api.sizeColumnsToFit()
  }

  const getRowStyle = (params: RowClassParams) => {
    const data = params.data
    if (!data || !data.showAlert) return

    // Only highlight the latest alert row
    if (!data.isLatestAlert) return

    if (data.alertType === IdeaAlertType.Info) {
      return { backgroundColor: themeStyle.colorInfoBg } as RowStyle
    } else if (data.alertType === IdeaAlertType.Warning) {
      return { backgroundColor: themeStyle.colorWarningBg } as RowStyle
    } else if (data.alertType === IdeaAlertType.Error) {
      return { backgroundColor: themeStyle.colorError } as RowStyle
    }
  }

  return (
    <IdeyaAgGridTable
      defaultColDef={defaultColDef}
      height={400}
      columns={columns}
      rowData={rowData}
      autoSizeStrategy={autoSizeStrategy}
      onGridReady={handleGridReady}
      getRowStyle={getRowStyle}
    />
  )
}
